import type { NetworkTest } from "@teamtable/api";
import { useCallback, useEffect, useRef, useState } from "react";

import { apiClient } from "../api/client.js";

export function DeviceNetworkTest({
  deviceId,
  canRun,
  onCompleted,
  deviceVersionCode,
}: {
  deviceId: string;
  canRun: boolean;
  onCompleted: () => void;
  deviceVersionCode?: number;
}) {
  const [test, setTest] = useState<NetworkTest>();
  const [configured, setConfigured] = useState(false);
  const [supported, setSupported] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string>();
  const completed = useRef<string | undefined>(undefined);
  const fetchTest = useCallback(async () => {
    try {
      const response = await apiClient["/api/v1/admin/devices/{id}/network-test"].get({
        parameters: { id: deviceId },
        body: null,
      });
      if (response.code !== 200) throw new Error("Unable to load speed-test status");
      setError(undefined);
      setTest(response.body.test);
      setConfigured(response.body.upload_configured);
      setSupported(response.body.test_supported);
      const next = response.body.test;
      if (
        next &&
        (next.status === "succeeded" || next.status === "failed") &&
        completed.current !== next.id
      ) {
        completed.current = next.id;
        onCompleted();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, deviceVersionCode, onCompleted]);
  useEffect(() => {
    void fetchTest();
  }, [fetchTest]);
  const inFlight = test?.status === "requested" || test?.status === "running";
  useEffect(() => {
    const interval = setInterval(() => void fetchTest(), inFlight ? 5000 : 30_000);
    return () => clearInterval(interval);
  }, [inFlight, fetchTest]);
  const run = async () => {
    setRequesting(true);
    setError(undefined);
    try {
      const response = await apiClient["/api/v1/admin/devices/{id}/network-test"].post({
        parameters: { id: deviceId },
        body: { id: crypto.randomUUID() },
      });
      if (response.code === 201) setTest(response.body);
      else setError(response.body.message);
    } catch {
      setError("Unable to request a speed test");
    } finally {
      setRequesting(false);
    }
  };
  return (
    <div className="mt-4 pt-4 border-t border-border">
      {test?.source === "scheduled" && (
        <p className="text-sm text-text-muted mb-2">Automatic speed test</p>
      )}
      {test && (
        <p className="text-sm text-text-secondary mb-3" role="status">
          {test.status === "requested"
            ? "Waiting for the device"
            : test.status === "running"
              ? "Speed test running"
              : test.status === "succeeded"
                ? "Last test completed"
                : `Last test failed: ${test.detail ?? "Unknown error"}`}
        </p>
      )}
      {canRun && (
        <button
          onClick={() => void run()}
          disabled={loading || !configured || !supported || inFlight || requesting}
          className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {requesting ? "Requesting…" : inFlight ? "Test in progress…" : "Run speed test"}
        </button>
      )}
      {!loading && !supported && (
        <p className="text-sm text-text-muted mt-2">Update the APK to enable speed tests.</p>
      )}
      {!loading && supported && !configured && (
        <p className="text-sm text-text-muted mt-2">Speed-test upload storage is not configured.</p>
      )}
      {error && (
        <p className="text-sm text-red-400 mt-2" role="alert">
          {error}
        </p>
      )}
      <p className="text-sm text-text-muted mt-3">
        Automatic tests run about every hour with jitter while online and idle. The device requires
        unmetered Wi-Fi. You can also request a test here. Each test uses up to 5 MiB through Google
        storage, with a five-minute cooldown between requests.
      </p>
    </div>
  );
}
